import { memo, useEffect, useId, useRef, useState } from "react";
import { Calendar, DatePicker } from "@progress/kendo-react-dateinputs";
import type {
  CalendarChangeEvent,
  DatePickerChangeEvent,
} from "@progress/kendo-react-dateinputs";
import { Popup } from "@progress/kendo-react-popup";
import "@/app/page/default/Dashboard/Dashboard.scss";
import { ICON_BASE_PATH } from "@/app/utils/config";

interface AppDatePickerMiniProps {
  placeholder: string;
  onChange: (event: Date | null) => void;
  value: Date | null;
  min?: Date;
  max?: Date;
}

export default memo(function AppDatePickerMini({
  value,
  placeholder,
  onChange,
  min,
  max,
}: Readonly<AppDatePickerMiniProps>) {
  const [show, setShow] = useState(false);

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const datePickerRef = useRef<any>(null);

  const popupId = useId();

  const handleOnChange = (
    event: DatePickerChangeEvent | CalendarChangeEvent,
  ) => {
    onChange(event.value);
    setShow(false);

    datePickerRef.current?.focus?.();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        anchorRef.current &&
        !anchorRef.current.contains(target) &&
        popupRef.current &&
        !popupRef.current.contains(target)
      ) {
        setShow(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShow(false);
        datePickerRef.current?.focus?.();
      }
    };

    if (show) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [show]);

  return (
    <div
      ref={anchorRef}
      className="gx-date-picker-mini"
      aria-haspopup="dialog"
      aria-expanded={show}
      aria-controls={popupId}
    >
      <div className="gx-date-picker-mini-main">
        <img
          src={`${ICON_BASE_PATH}past-due-calender.svg`}
          alt=""
          aria-hidden="true"
        />

        <DatePicker
          ref={datePickerRef}
          value={value}
          onChange={handleOnChange}
          onFocus={() => setShow(true)}
          placeholder={placeholder}
          format="dd MMM yyyy"
          min={min}
          max={max}
          aria-expanded={show}
          aria-controls={popupId}
        />
      </div>

      <Popup anchor={anchorRef.current} show={show}>
        <div ref={popupRef} id={popupId} role="dialog" aria-label="Select date">
          <Calendar
            value={value}
            min={min}
            max={max}
            onChange={handleOnChange}
          />
        </div>
      </Popup>
    </div>
  );
});
